import { Component, inject, OnInit } from '@angular/core';
import { AppShellNoSSRDirective, LazyLoadImagesDirective } from 'ngx-unificator/directives';
import { Meta } from '@angular/platform-browser';
import { HostRequestsInterceptor } from '../core/interceptors/host-requests.interceptor';
import { Router, RouterOutlet } from '@angular/router';
import { ToastMessageComponent } from '../core/shared/modules/toast-message/toast-message.component';
import { GameIframeComponent } from '../core/shared/components/game-iframe/game-iframe.component';
import { isMobile } from '../core/helpers/device';
import { StickyMenuComponent } from '../core/shared/components/sticky-menu/sticky-menu.component';
import { UserService } from '../core/services/user/user.service';
import { combineLatest, filter, first, merge, takeWhile, timer } from 'rxjs';
import { SsPaymentsV2Service } from '../core/vendor/ss-payments-v2/ss-payment.service';
import { CookieService, LocalstorageService, PlatformService } from 'ngx-unificator/services';
import { GlobalEventsService } from '../core/services/global-events.service';
import { delay, distinctUntilChanged, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { ROUTES_TO_HIDE_STICKY_MENU } from '../core/helpers/redirect-data';
import { UserBonusesService } from '../core/services/user/user-bonuses.service';
import { EXCLUDE_PUSH_PROMPT_PAGES } from '../core/services/one-signal.service';
import { GamesFilterService } from '../core/services/games/games-filter.service';
import {
  CookieMessageContentComponent,
} from '../core/shared/modules/toast-message/content/cookie-message-content/cookie-message-content.component';
import { ToastMessageService } from '../core/shared/modules/toast-message/toast-message.service';
import { WrSocketService } from '../core/services/wr-socket.service';
import { WebSocketService } from '../core/services/web-socket.service';
import { BankingService } from '../core/services/banking.service';
import { InstallAppService } from '../core/services/install-app.service';
import { InstallAppBannerComponent } from '../core/shared/components/install-app-banner/install-app-banner.component';
import { CommonDataService } from '../core/services/common-data.service';
import { BonusStoreService } from '../core/services/bonus-store.service';
import { UserVipService } from '../core/services/user/user-vip.service';
import { AbTestNewService } from '../core/ab-test/ab-test.service';
import { AB_TEST_LIST } from '../core/ab-test/ab-test.data';
import { EnvironmentService } from '../core/services/environment.service';
import { StaticContentService } from '../core/services/static-content.service';
import { ModalService } from '../core/modal/modal.service';
import { BonusShopEnum } from './bonus-shop/bonus-shop.types';
import { SummerService } from './promo/summer/summer.service';

declare var OneSignal;

@Component({
  selector: 'app-page',
  templateUrl: './page.component.html',
  styleUrls: ['./page.component.scss'],
  imports: [
    LazyLoadImagesDirective,
    RouterOutlet,
    AppShellNoSSRDirective,
    ToastMessageComponent,
    GameIframeComponent,
    StickyMenuComponent,
    InstallAppBannerComponent,
  ],
})
export class PageComponent implements OnInit {
  private _user: UserService = inject(UserService);
  private _meta: Meta = inject(Meta);
  private _hostInterceptor = inject(HostRequestsInterceptor);
  private _ssPayment: SsPaymentsV2Service = inject(SsPaymentsV2Service);
  private _storage: LocalstorageService = inject(LocalstorageService);
  private _platform: PlatformService = inject(PlatformService);
  private _globalEvents: GlobalEventsService = inject(GlobalEventsService);
  private _router: Router = inject(Router);
  private _bonuses: UserBonusesService = inject(UserBonusesService);
  private _cookies: CookieService = inject(CookieService);
  private _toast: ToastMessageService = inject(ToastMessageService);
  private _wrWebsocket: WrSocketService = inject(WrSocketService);
  private _ws: WebSocketService = inject(WebSocketService);
  private _banking: BankingService = inject(BankingService);
  private _installApp: InstallAppService = inject(InstallAppService);
  private _commonData: CommonDataService = inject(CommonDataService);
  private _env: EnvironmentService = inject(EnvironmentService);
  private _static: StaticContentService = inject(StaticContentService);
  private _bonusShop = inject(BonusStoreService);
  private _ab: AbTestNewService = inject(AbTestNewService);
  private _modal: ModalService = inject(ModalService);
  private _vip = inject(UserVipService);
  private _summer = inject(SummerService);
  public gamesFilters: GamesFilterService = inject(GamesFilterService);

  protected readonly isMobile = isMobile;
  public stickyMenuVisible: boolean = false;
  /**
   * Is install app visible
   */
  public installAppVisible: boolean;

  constructor() {
    this._ws.createConnection();
  }

  ngOnInit() {
    this._loadUserVipStore();
    this._initBonusShop();
    this._authHandler();
    this._notAuthHandler();
    this._handleRoute();
    this._listenOnesignal();
    this._listenUserBalance();
    this._setPageVerificationCode();
    this._banking.getAllPayments();

    if (this._platform.isBrowser) {
      this._initSummerPromo();
    }
  }

  private _initSummerPromo() {
    this._user.auth$.pipe(
      switchMap(() => this._summer.initPromo$()),
    ).subscribe();
  }

  private _listenUserBalance() {
    this._ws.userBalance$.pipe(
      map(balance => {
        return {
          ...balance,
          amount: this._commonData.subunitsToUnits(balance.amount_cents, balance.currency),
        };
      }),
      tap((balance) => this._user.currentCurrency.amount = balance.amount),
    ).subscribe();
  }

  private _initBonusShop() {
    this._bonusShop.bonusStorePage$ = combineLatest([
        this._bonusShop.getAndMappedPage$(BonusShopEnum.COINS),
        this._bonusShop.getAndMappedPage$(BonusShopEnum.CASH),
      ],
    ).pipe(
      shareReplay(1),
    );
  }

  /**
   * Auth handler
   */
  private _authHandler() {
    if (this._platform.isBrowser) {
      this._user.auth$
        .pipe(
          filter(auth => !!auth),
          first(),
        )
        .subscribe(auth => {
          this._ab.syncUserABTestGroupOnAuth();
          this._ssPayment.loadApiLibrary();
          this._bonuses.updateBonusList();
          this._storage.set('user_id', this._user.info.id);
          this._wrWebsocket.initWrSocket();
          this._initUnusedBonusesForNotification();
        });
    }

    if (!this._cookies.check('isCookiesAccepted')) {
      this.showCookiesMessage();
    }
  }

  /**
   * Actions for not auth user
   * @private
   */
  private _notAuthHandler() {
    this._user.auth$
      .pipe(
        filter(auth => !auth),
        first(),
      )
      .subscribe(() => {
        this._initSlotModal();
      });
  }

  private _handleRoute() {
    this._globalEvents.routerNavigationEnd$.pipe(startWith({ url: this._router.url })).subscribe(route => {
      this.stickyMenuVisible = !ROUTES_TO_HIDE_STICKY_MENU.some(urlPart => route.url.includes(urlPart));
      this.installAppVisible = route.url.split('/').length === 2 && this._user.auth &&
        !(this._installApp.isNativeApp || this._installApp.hideInstallAppByCMS || !this._installApp.showBottomBanner);
    });
  }

  /**
   * To show OneSignal prompt popup on specific pages
   */
  private _listenOnesignal() {
    this._globalEvents.routerNavigationEnd$.pipe(
      distinctUntilChanged(),
      takeWhile(() => !OneSignal?.User?._currentUser?.hasOneSignalId, true),
      filter((data) => EXCLUDE_PUSH_PROMPT_PAGES.every(page => !data?.url?.includes(page))),
      delay(1000),
      tap(() => OneSignal.Slidedown.promptPush()),
      tap(() => this._cookies.set('user_push_subscribe', `${!!(OneSignal?.User?._currentUser?.hasOneSignalId && OneSignal?.User?.PushSubscription?.optedIn)}`, 999, '/')),
    ).subscribe();
  }

  private _initUnusedBonusesForNotification() {
    this._bonuses.bonuses$.pipe(
      first(),
      filter(list => list.unused && list.unused.length),
      map(list => list.unused),
    ).subscribe();
  }

  private _loadUserVipStore() {
    merge(
      this._vip.loadVipPage$().pipe(
        tap((page) => {
          this._vip.vipStore.storeSignal.update(store => ({
            ...store,
            page,
          }));
        }),
      ),
      this._vip.vipLevels$,
    ).subscribe();
    this._vip.reloadVipLevels();
  }

  /*
 * Show message until user accepts cookies
 */
  public showCookiesMessage() {
    if (this._platform.isBrowser) {
      const message = this._toast.info(CookieMessageContentComponent, null, true, false, false);
      message.callback = () => {
        requestAnimationFrame(() => {
          if (!this._cookies.check('isCookiesAccepted')) {
            this.showCookiesMessage();
          }
        });
      };
    }
  }

  /**
   * Private init slot modal
   * @private
   */
  private _initSlotModal() {
    if (this._platform.isBrowser) {
      const routesIgnore = ['register', 'login', '403', '404', 'maintenance'];

      timer(5000)
        .pipe(
          takeWhile(() => !this._user.auth),
          filter(() => !this._env.isSe),
          filter(() => !routesIgnore.some(route => this._router.url.includes(route)) && !this._cookies.check('--slots-modal-display')),
          switchMap(() => this._ab.loadABTest$('DEVSP576')),
          filter(abTest => abTest === AB_TEST_LIST.DEVSP576.abTestGroupIds.V2),
          switchMap(() => combineLatest([this._static.item({ slug: 'sp-slot-welcome' }), this._bonuses.cmsBonusList$])),
          filter(([modalData, welcomeBonusList]) => modalData?.[0] && !!welcomeBonusList?.length),
          tap(async ([modalData, welcomeBonus]) => {
            if (!this._user.auth) {
              const component = await import('../core/modal/components/lazy/slots-welcome-modal/slots-welcome-modal.component');
              await this._modal.openLazy(component.SlotsWelcomeModalComponent, {
                template: 'CLEAR',
                data: { welcomeBonus: welcomeBonus[0], modalData: modalData[0] },
              });
            }
          }),
        )
        .subscribe();
    }
  }

  private _setPageVerificationCode() {
    if (this._hostInterceptor.getDomainName().includes('spinpanda1')) {
      this._meta.updateTag({
        name: 'dmca-site-verification',
        content: 'MU5seEh3c2JGZUNaM0wvTTErdTVmUFRsb0tXVG1CdHVHOUtjVmwyakZCdz01',
      });
    }
  }
}
